// ## Gridform Adapter Class
import _ from 'lodash'
import { ATYPES } from '@src/plugins/vue-gridform/state/types'
import $ from '@src/plugins/vue-gridform/shared/helper'
import FIELDTYPES from '@src/plugins/vue-gridform/shared/fieldtypes'

const _OMITTED_EXPORT_PROPS = ['fieldstyle', 'clearable', 'autofocus', 'placeholder']
const _OMITTED_EDIT_PROPS = ['name', 'fieldstyle', 'clearable', 'autofocus', 'placeholder']
const _PICKED_RATING_PROPS = ['min', 'max', 'score', 'failed', 'noRating']
const _PICKED_FIELDLIST_PROPS = ['id', 'initials', 'name', 'description', 'collapsed', 'internal', 'noRating', 'weighting', 'export', 'activate', 'activated']

const _isField = fld => {
    return _.has(fld, 'name') && _.has(fld, 'value')
}
const _isFieldlist = fl => {
    return _.has(fl, 'fieldlist') && _.has(fl, 'rows') && _.has(fl, 'index')
}

// convenience methods to handle gridform exposed to vm.$gridform[gfname]
export default class GF {
    constructor(gf) {
        this._uuid = _.uniqueId('gfc-')
        this.name = gf.name

        // ## STORE
        this.dispatch = gf.dispatch.bind(gf)
        this.reset = gf.reset.bind(gf)
        this.config = config => {
            if (!config) return _.cloneDeep(gf.config)
            else this.dispatch(ATYPES.GFCONFIG, config)
        }
        this.update = _.partial(this.dispatch, ATYPES.GFCHANGE)

        // ## FIELD DATA
        this.values = data => this.update($.objectToPayload(data))
        this.data = () => _.cloneDeep(gf.formdata)
        this.validate = gf.validate.bind(gf)
        this.isValid = () => {
            const validation = gf.$store.getters[$.ns('validation', gf.name)]
            return _.get(validation, 'valid')
        }

        // ## ROWS
        this.rows = ({ rows, editable, uuid } = {}) => {
            // if rows given set them as new rows
            if (Array.isArray(rows)) {
                this.dispatch(ATYPES.GFSETROWS, {rows, uuid})
                return
            }

            const gfRows = _.cloneDeep(gf.rows || []) // clone rows before form is resetted or destroyed

            // only fields that are editable with the gridform builder
            if (editable) {
                return gfRows.map(row => {
                    return row.map(col => {
                        // get prop keys that are editable for the current type
                        /** @type string[] */
                        const tp = _.flatten(FIELDTYPES.type(col.type))

                        // add important type props for restore
                        tp.push('type')
                        tp.push('value')
                        tp.push('fieldlist')
                        tp.push('rating')
                        tp.push('ratingScroll')
                        tp.push('dense')
                        tp.push('forceDisabled')
                        tp.push('forceEnabled')
                        tp.push('weighting')
                        tp.push('export')

                        // pick only valid props from current field
                        const tmp = _.clone(col)
                        tmp.fieldlist = _.pick(tmp.fieldlist, _PICKED_FIELDLIST_PROPS)
                        // remove calculated props from col.rating (like colors and percent and label)
                        if (tmp.rating) tmp.rating = _.pick(tmp.rating, _PICKED_RATING_PROPS)

                        const exportCol = _.pick(_.omit(tmp, _OMITTED_EXPORT_PROPS), tp)
                        // remove default type
                        if (exportCol.type === 'text') delete exportCol.type
                        // minimize size and remove all empty and computed props
                        if (_.isEmpty(exportCol.fieldlist)) delete exportCol.fieldlist
                        // rating = false must be exported
                        if (exportCol.rating !== false && _.isEmpty(exportCol.rating)) delete exportCol.rating
                        // remove unsuccessful props
                        if (!exportCol.required) delete exportCol.required
                        if (!exportCol.readonly) delete exportCol.readonly
                        if (!exportCol.dense) delete exportCol.dense
                        if (!exportCol.forceDisabled) delete exportCol.forceDisabled
                        if (!exportCol.forceEnabled) delete exportCol.forceEnabled
                        if (!exportCol.export) delete exportCol.export
                        // remove computed tick labels
                        if (exportCol.tickLabels) {
                            const defaultTickLabels = _.range(exportCol.min, exportCol.max + 1, exportCol.step)
                            if (_.isEqual(exportCol.tickLabels, defaultTickLabels)) delete exportCol.tickLabels
                        }
                        return exportCol
                    })
                })
            }

            // default: return a copy of current rows
            return gfRows
        }
        this.add = (payload) => {
            let rows = payload.rows
            if (!rows && payload.row) {
                if (Array.isArray(payload.row)) rows = [payload.row]
                else rows = [[payload.row]]
            }
            const fieldlist = payload.fieldlist
            this.dispatch(ATYPES.GFADDROWS, { rows, fieldlist })
        }
        this.clear = () => this.rows({ rows: [] })
        this.export = () => this.rows({ editable: true })

        // ## ACTIONS
        this.edit = (fieldName, append) => {
            const field = _.first(this.field({ name: fieldName }))
            if (!field) return

            const fieldData = gf.$store.getters[$.ns('fieldData', this.name)](fieldName)

            const type = _.get(fieldData, 'def.type', 'text')
            const i18n = (key) => {
                return gf.$vuetify.lang.t(`$vuetify.gfb.${key}`)
            }
            const rows = FIELDTYPES.props(type, field, _OMITTED_EDIT_PROPS, i18n)
            if (_.isEmpty(rows)) return // no rows for editing defined for that type

            this.update({[fieldName]: { editMode: true }})

            const items = _.get(fieldData, 'def.items')
            if (items) field.items = items

            _.each(rows, row => {
                if (row.length === 1 && row[0].name === 'label') row[0].size = 12
                if (row.length === 1 && row[0].name === 'placeholder') row[0].size = 12
            })

            rows.push([{
                name: 'export',
                type: 'checkbox',
                label: i18n('field.export'),
                hint: i18n('field.exportHint'),
                value: field.export,
                size: 6
            }])

            if (!_.isEmpty(append)) rows.push(append)

            // set config and add rows to field edit gridform
            const gfName = gf.$refs['gffl'].gfFieldName(field)
            const name = i18n('field.editTitle')
            const fieldlist = {id: _.uniqueId('vgfb-fl-'), name, foldable: false, moveable: false}
            gf.$nextTick(async () => {
                await gf.$store.dispatch($.ns(ATYPES.GFCONFIG, gfName), gf.editConfig)
                await gf.$store.dispatch($.ns(ATYPES.GFSETROWS, gfName), {rows, fieldlist})
                gf.$autofocus(`#vgf-${gfName}`)
            })
        }
        this.select = ({all} = {}) => {
            const fieldsData = gf.$store.getters[$.ns('fieldsData', this.name)]
            if (typeof all === 'boolean') {
                const update = {}
                _.each(fieldsData, (fd, fname) => {
                    update[fname] = {selected: all}
                })
                this.dispatch(ATYPES.GFDATA, update)
                return
            }

            const selected = []
            _.each(fieldsData, (fd, fname) => {
                if (fd.selected) selected.push(fname)
            })
            return selected
        }

        // ## QUERY
        this.field = (filter) => {
            const fields = _.flatten(gf.rows)
            if (!filter) return _.cloneDeep(fields)
            return _.filter(fields, filter)
        }
        this.keys = () => _.cloneDeep(gf.fieldsKeys)

        // ## FIELDLIST
        this.fieldlist = data => {
            if (!data) return _.cloneDeep(gf.fieldLists)
            else this.dispatch(ATYPES.GFFIELDLIST, data)
        }
        this.fieldlists = data => {
            return this.dispatch(ATYPES.GFFIELDLISTS, data)
        }
        this.fieldlistRemove = ({ id } = {}) => {
            if (!id) return
            // remove all fields from given fieldlist id
            const rows = this.rows().filter(row => {
                // check first field in row
                // all fields from one fieldlist must be in the same row
                return _.get(row, '0.fieldlist.id') !== id
            })
            this.rows({rows})
        }
        this.fieldlistFocus = ({ command } = {}) => {
            const flSize = _.size(gf.fieldLists)
            const focusedFl = gf.fieldLists.find(fl => _.get(fl, 'fieldlist.focused'))
            if (!command) return focusedFl
            if (!_.includes(['next', 'prev', 'first', 'last'], command)) return

            let focus
            // find fieldlist to focus
            if (command === 'next') {
                if (focusedFl.index + 1 < flSize) focus = gf.fieldLists[focusedFl.index + 1]
                else focus = gf.fieldLists[0]
            } else if (command === 'prev') {
                if (focusedFl.index - 1 >= 0) focus = gf.fieldLists[focusedFl.index - 1]
                else focus = gf.fieldLists[flSize - 1]
            } else if (command === 'first') {
                if (focusedFl.index > 0) focus = gf.fieldLists[0]
            } else if (command === 'last') {
                if (focusedFl.index < flSize - 1) focus = gf.fieldLists[flSize - 1]
            }

            if (focus) this.fieldlist({ focused: focus.fieldlist.id })
        }
        this.fieldlistMove = ({id, command} = {}) => {
            if (!_.includes(['up', 'down', 'first', 'last'], command)) return

            // which fieldlist, given id or focused?
            let fl
            if (id) fl = gf.fieldLists.find(fl => _.get(fl, 'fieldlist.id') === id)
            else fl = gf.fieldLists.find(fl => _.get(fl, 'fieldlist.focused'))

            const from = fl.index
            const flSize = _.size(gf.fieldLists)
            let to
            if (command === 'up' && from > 0) to = from - 1
            else if (command === 'down' && from < flSize - 1) to = from + 1
            else if (command === 'first' && from > 0) to = 0
            else if (command === 'last' && from < flSize - 1) to = flSize - 1

            if (!_.isUndefined(to)) {
                // move fieldlist to new position
                const fls = _.cloneDeep(gf.fieldLists)
                fls.splice(to, 0, fls.splice(from, 1)[0])
                // concat rows in new order
                let rows = []
                _.each(fls, fl => {
                    rows = rows.concat(fl.rows)
                })
                this.rows({rows})
            }
        }

        // ## RATING
        this.rating = (options) => {
            if (!options) return $.ratingsGrouped(gf.rows)
            if (_.isEmpty(options.item)) return // required prop
            if (!_isField(options.item) && !_isFieldlist(options.item)) return // invalid item syntax

            if (options.min || options.max) {
                const setMax = !!options.max
                const update = setMax ? $.ratingMax(options.item) : $.ratingMin(options.item) // can be a fieldlist or a field
                this.update(update) // set values for fieldlist rows or field to max score
                // now let the fieldlist update rating
                const flid = _.get(options, 'item.fieldlist.id', 'default')
                if (flid) this.fieldlist({id: flid, isMax: setMax}) // set flag to fieldlist for toggling min and max
            }
            else if (options.up || options.down) {
                const field = options.item
                const rating = field.rating
                let score = options.up ? rating.score + 1 : rating.score - 1
                if (score > rating.max) score = rating.max
                if (score < rating.min) score = rating.min
                const update = {[field.name]: {rating: {...rating, score}}}
                this.update(update) // set new score
            }
        }

        // ## STATES
        this.enable = fields => {
            this.update($.arrayToPayload(fields, {disabled: false}))
        }
        this.disable = fields => {
            this.update($.arrayToPayload(fields, {disabled: true}))
        }

        this.show = fields => {
            this.update($.arrayToPayload(fields, {hidden: false}))
        }
        this.hide = fields => {
            this.update($.arrayToPayload(fields, {hidden: true}))
        }

        this.include = fields => {
            this.update($.arrayToPayload(fields, {exclude: false, hidden: false}))
        }
        this.exclude = fields => {
            this.update($.arrayToPayload(fields, {exclude: true, hidden: true}))
        }

        this.lock = () => this.config({disabled: true, loading: true})
        this.unlock = () => this.config({disabled: false, loading: false})
    }
}
